/* eslint-disable react/jsx-pascal-case */
/* eslint-disable jsx-a11y/alt-text */
import { useState, useEffect, useCallback } from "react";
import {
  TextField,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { ArrowUpward as ArrowUpIcon, Search as SearchIcon, ImageNotSupported as NoImageIcon } from "@mui/icons-material";
import { animateScroll as scroll } from "react-scroll";
import "../Styles/HomeShop.css";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../utils/formatters";
import { apiClient } from "../Api/AllApi";

/** Shows product image only when URL exists and image loads; otherwise a placeholder (no broken image). */
function ProductImage({ imageUrl, alt, className }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const hasUrl = Boolean(imageUrl && String(imageUrl).trim());

  useEffect(() => {
    if (!hasUrl) {
      setError(true);
      setLoaded(false);
      return;
    }
    setError(false);
    setLoaded(false);
  }, [hasUrl, imageUrl]);

  if (!hasUrl || error) {
    return (
      <div className={`${className} product-image-placeholder`} aria-hidden>
        <NoImageIcon className="placeholder-icon" />
        <span className="placeholder-text">No image</span>
      </div>
    );
  }

  return (
    <div className="product-image-wrap">
      <CardMedia
        component="img"
        image={`${apiClient.defaults.baseURL}/storage/${imageUrl}`}
        alt={alt}
        className={className}
        style={{ opacity: loaded ? 1 : 0 }}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
      />
      {!loaded && (
        <div className="product-image-placeholder product-image-loading" aria-hidden>
          <CircularProgress size={32} />
        </div>
      )}
    </div>
  );
}

const HomeShop = ({ handleClick }) => {
  const { t } = useTranslation();

  const [dataSource, setDataSource] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nameFilter, setNameFilter] = useState("");
  const [showScrollButton, setShowScrollButton] = useState(false);
  const fetchProducts = useCallback(() => {
    setLoading(true);
    apiClient.get("/products")
      .then((response) => {
        setLoading(false);
        if (response.data && Array.isArray(response.data)) {
          setDataSource(response.data);
        } else {
          setDataSource([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setLoading(false);
        setDataSource([]);
      });
  }, []);

  const filterProducts = useCallback(
    (products, filterText) => {
      return products
        .filter((item) => {
          const name = JSON.parse(item.name)?.en || "Unknown";
          return name.toLowerCase().includes(filterText.toLowerCase());
        })
        .sort((a, b) => a.stock - b.stock);
    },
    []
  );

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    setFilteredData(filterProducts(dataSource, nameFilter));
  }, [dataSource, nameFilter, filterProducts]);

  const scrollToTop = useCallback(() => {
    scroll.scrollToTop();
  }, []);

  const handleScroll = useCallback(() => {
    setShowScrollButton(window.scrollY > window.innerHeight);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  const handleNameFilterChange = useCallback((event) => {
    setNameFilter(event.target.value);
  }, []);

  return (
    <div
      className="body-Home"
      style={{
        direction: "rtl",
        position: 'relative',
      }}
    >
      <h1 className="heading">{t("allProducts")}</h1>

      <TextField
        className="Search"
        label={t("searchPlaceholder")}
        variant="outlined"
        fullWidth
        value={nameFilter}
        onChange={handleNameFilterChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
        style={{
          marginTop: "16px",
          direction: "rtl",
          textAlign: "right",
        }}
      />

      {loading ? (
        <div className="loading">
          <CircularProgress size={48} />
          <Typography className="loading-text">{t("loading") || "Loading..."}</Typography>
        </div>
      ) : filteredData.length === 0 ? (
        <div className="empty-state" role="status">
          <NoImageIcon sx={{ fontSize: 64 }} color="disabled" />
          <Typography variant="h6" className="empty-state-title">
            {nameFilter ? t("noResults") || "No products match your search" : t("noProducts") || "No products yet"}
          </Typography>
          <Typography color="textSecondary" className="empty-state-hint">
            {nameFilter ? t("tryDifferentSearch") || "Try a different search term" : ""}
          </Typography>
        </div>
      ) : (
        <Grid container spacing={3} className="product-list">
          {filteredData.map((item) => {
            const productName = JSON.parse(item.name)?.en || "Unknown";
            const outOfStock = item.stock === 0;
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                <Card className={`itemCard ${outOfStock ? "out-of-stock-card" : ""}`} elevation={0}>
                  <ProductImage
                    imageUrl={item.image_url}
                    alt={productName}
                    className="Image_Card"
                  />
                  <CardContent className={outOfStock ? "out-of-stock" : ""}>
                    <Typography variant="subtitle1" className="product-name" component="h3">
                      {productName}
                    </Typography>
                    <Typography variant="h6" className="product-price">
                      {formatCurrency(item.price)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" className="product-meta">
                      {t("sku")}: {item.sku} · {t("stock")}: {item.stock}
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      className="add-to-cart-btn"
                      onClick={() => {
                        if (item.stock > 0) handleClick(item);
                      }}
                      disabled={outOfStock}
                      aria-label={outOfStock ? t("outOfStock") : `${t("addToCart")} ${productName}`}
                    >
                      {outOfStock ? t("outOfStock") : t("addToCart")}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {showScrollButton && (
        <IconButton
          color="primary"
          className="scroll-to-top-button"
          onClick={scrollToTop}
        >
          <ArrowUpIcon />
        </IconButton>
      )}
    </div>
  );
};

export default HomeShop;